import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { format } from "date-fns";
import {
  Book, ChevronLeft, ChevronRight, X, MapPin, Calendar,
  Image as ImageIcon, MessageCircle, Maximize2,
  Heart, Users, Star, Check, Move
} from "lucide-react";
import { ImagePositionEditor, getImageTransform } from "./ImagePositionEditor";
import AuthedImage, { getPhotoBlobUrl } from "./AuthedImage";

export const Scrapbook = ({ isOpen, onClose }) => {
  const { token, user } = useAuth();
  const [scrapbook, setScrapbook] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [slideshowIndex, setSlideshowIndex] = useState(0);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [slideshowPhotos, setSlideshowPhotos] = useState([]);
  const [selectingCover, setSelectingCover] = useState(null);
  const [scrapbookTab, setScrapbookTab] = useState("mine");
  const [sharedScrapbook, setSharedScrapbook] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [sharedMeta, setSharedMeta] = useState({});
  
  // Image positioning state
  const [editingImage, setEditingImage] = useState(null);
  const [imagePositions, setImagePositions] = useState({});

  // Load saved positions from backend (with localStorage fallback)
  // Re-fetches whenever scrapbook data changes (covers login/logout/refresh)
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const res = await axios.get(`${API}/image-positions`);
        if (res.data?.positions && Object.keys(res.data.positions).length > 0) {
          setImagePositions(res.data.positions);
          // Sync to localStorage for offline fallback
          localStorage.setItem('scrapbook-image-positions', JSON.stringify(res.data.positions));
          return;
        }
      } catch (e) {}
      // Fallback: load from localStorage
      const saved = localStorage.getItem('scrapbook-image-positions');
      if (saved) {
        try { setImagePositions(JSON.parse(saved)); } catch (e) {}
      }
    };
    loadPositions();
  }, [scrapbook]);

  const fetchScrapbook = async () => {
    try {
      const res = await axios.get(`${API}/memories/scrapbook`);
      setScrapbook(res.data.scrapbook);
      // No more bulk Promise.all of all cover photos — AuthedImage's
      // IntersectionObserver fetches them as they enter the viewport.
    } catch (error) {
      console.error("Failed to fetch scrapbook");
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedScrapbook = async () => {
    setSharedLoading(true);
    try {
      const res = await axios.get(`${API}/memories/scrapbook/shared`);
      setSharedScrapbook(res.data.scrapbook || []);
      setSharedMeta({
        partner_name: res.data.partner_name,
        shared_countries: res.data.shared_countries,
        shared_trips: res.data.shared_trips,
      });
      // No more bulk Promise.all — viewport-driven fetches inside AuthedImage.
    } catch (e) {
      console.debug("Shared scrapbook fetch failed");
    } finally {
      setSharedLoading(false);
    }
  };



  const setCoverPhoto = async (memoryId, photoId) => {
    try {
      await axios.post(`${API}/items/${memoryId}/cover-photo/${photoId}`);
      toast.success("Cover photo updated!");
      setSelectingCover(null);
      fetchScrapbook();
    } catch (error) {
      toast.error("Failed to set cover photo");
    }
  };

  const handleSavePosition = (memoryId, position) => {
    const newPositions = { ...imagePositions, [memoryId]: position };
    setImagePositions(newPositions);
    // Persist to backend (primary) and localStorage (fallback)
    localStorage.setItem('scrapbook-image-positions', JSON.stringify(newPositions));
    axios.post(`${API}/image-position`, { item_id: memoryId, position }).catch(() => {});
    toast.success("Photo position saved!");
  };

  // Slideshow loads ONLY the photos the user opened, in parallel, full-res.
  // Uses the shared photo cache so a photo already loaded as a feed thumb
  // is not re-fetched here either (different cache key — but the second
  // open of the same slideshow reuses the download blob).
  const fetchSlideshowPhotos = async (memory) => {
    if (!memory.photos || memory.photos.length === 0) return;
    const settled = await Promise.all(
      memory.photos.map(async (photo) => {
        try {
          const url = await getPhotoBlobUrl(photo.id, "download");
          return { id: photo.id, url, caption: photo.caption };
        } catch (e) {
          return null;
        }
      })
    );
    setSlideshowPhotos(settled.filter(Boolean));
    setSlideshowIndex(0);
    setShowSlideshow(true);
  };

  useEffect(() => {
    if (isOpen) {
      fetchScrapbook();
      if (user?.partner_id) fetchSharedScrapbook();
    }
    // No URL.revokeObjectURL cleanup — blob URLs are now owned by the
    // module-level cache in AuthedImage so they survive dialog close
    // and the next open is INSTANT (cache hit).
  }, [isOpen]);

  const nextSlide = () => {
    setSlideshowIndex((prev) => (prev + 1) % slideshowPhotos.length);
  };

  const prevSlide = () => {
    setSlideshowIndex((prev) => (prev - 1 + slideshowPhotos.length) % slideshowPhotos.length);
  };

  const groupedByYear = scrapbook.reduce((acc, memory) => {
    const dateStr = memory.display_date || memory.completed_at;
    const year = dateStr ? dateStr.slice(0, 4) : "Unknown";
    if (!acc[year]) acc[year] = [];
    acc[year].push(memory);
    return acc;
  }, {});

  return (
    <>
      <Dialog open={isOpen && !showSlideshow} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl bg-card border-none rounded-2xl p-0 max-h-[90vh] overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b border-charcoal/5">
            <DialogTitle className="font-serif text-xl text-charcoal flex items-center gap-2">
              <Book className="w-5 h-5 text-primary" />
              Memory Scrapbook
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Your adventures captured in photos and memories
            </DialogDescription>
            {user?.partner_id && (
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => setScrapbookTab("mine")}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${scrapbookTab === "mine" ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                >
                  My Memories
                </button>
                <button
                  onClick={() => setScrapbookTab("shared")}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${scrapbookTab === "shared" ? "bg-pink-500 text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                >
                  <Heart className="w-3.5 h-3.5 inline mr-1" />
                  Shared
                </button>
              </div>
            )}
          </DialogHeader>

          <ScrollArea className="max-h-[75vh]">
            <div className="p-6">
              {scrapbookTab === "mine" ? (
              <>
              {loading ? (
                <div className="text-center py-12 text-muted-foreground">
                  Loading your memories...
                </div>
              ) : scrapbook.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-4">
                    <Book className="w-10 h-10 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-charcoal mb-2">
                    No memories yet
                  </h3>
                  <p className="text-muted-foreground">
                    Complete bucket list items and add photos to build your scrapbook
                  </p>
                </div>
              ) : (
                <div className="space-y-12">
                  {Object.entries(groupedByYear)
                    .sort(([a], [b]) => b.localeCompare(a))
                    .map(([year, memories]) => (
                      <div key={year}>
                        <h2 className="font-serif text-2xl font-medium text-charcoal mb-6 flex items-center gap-3">
                          <span className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-lg">
                            {year.slice(2)}
                          </span>
                          {year}
                        </h2>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {memories.map((memory, gridIdx) => {
                            const position = imagePositions[memory.id] || { x: 0, y: 0, scale: 1 };
                            // Eager-load the first 4 cards so the
                            // above-the-fold experience is instant.
                            const eager = gridIdx < 4;

                            return (
                              <div
                                key={memory.id}
                                className="group relative bg-background rounded-2xl overflow-hidden border border-charcoal/5 hover:shadow-lg transition-shadow"
                                data-testid={`scrapbook-memory-${memory.id}`}
                              >
                                {/* Photo or Placeholder - tap to adjust */}
                                <div
                                  className="relative h-48 bg-muted overflow-hidden cursor-pointer"
                                  onClick={async () => {
                                    if (!memory.photos?.length) return;
                                    const coverPhotoId = memory.cover_photo_id || memory.photos[0].id;
                                    try {
                                      const url = await getPhotoBlobUrl(coverPhotoId, "feed");
                                      setEditingImage({ memoryId: memory.id, imageUrl: url });
                                    } catch (e) {/* photo unavailable */}
                                  }}
                                >
                                  {memory.photos?.length > 0 ? (
                                    <>
                                      <AuthedImage
                                        photoId={memory.cover_photo_id || memory.photos[0].id}
                                        alt={memory.title}
                                        className="w-full h-full"
                                        imgStyle={getImageTransform(position)}
                                        eager={eager}
                                        testId={`scrapbook-cover-${memory.id}`}
                                      />

                                      {/* Adjust affordance — small bottom-right corner icon.
                                          Always softly visible (mobile has no hover) but tiny + dark
                                          so it never blocks the photo subject. */}
                                      <div
                                        className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center opacity-60 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-md"
                                        aria-hidden="true"
                                        data-testid="scrapbook-adjust-hint"
                                      >
                                        <Move className="w-3.5 h-3.5" />
                                      </div>
                                    </>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <ImageIcon className="w-12 h-12 text-muted-foreground/30" />
                                    </div>
                                  )}
                                  
                                  {/* Cover photo indicator */}
                                  {memory.cover_photo_id && (
                                    <div className="absolute top-3 left-3 px-2 py-1 bg-amber-500/90 rounded-full text-white text-xs flex items-center gap-1">
                                      <Star className="w-3 h-3 fill-current" />
                                      Cover
                                    </div>
                                  )}
                                  
                                  {/* Overlay with photo count */}
                                  {memory.photo_count > 1 && (
                                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 rounded-full text-white text-xs flex items-center gap-1">
                                      <ImageIcon className="w-3 h-3" />
                                      {memory.photo_count}
                                    </div>
                                  )}
                                  
                                  {/* Action buttons */}
                                  <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {/* Set Cover Photo button */}
                                    {memory.photo_count > 1 && memory.created_by === user?.id && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="bg-black/40 hover:bg-amber-500 text-white h-8 w-8"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectingCover(memory);
                                        }}
                                        title="Choose cover photo"
                                      >
                                        <Star className="w-4 h-4" />
                                      </Button>
                                    )}
                                    
                                    {/* Slideshow button */}
                                    {memory.photo_count > 0 && (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="bg-black/40 hover:bg-black/60 text-white h-8 w-8"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchSlideshowPhotos(memory);
                                        }}
                                      >
                                        <Maximize2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Content */}
                                <div 
                                  className="p-4 cursor-pointer"
                                  onClick={() => setSelectedMemory(memory)}
                                >
                                  <h3 className="font-medium text-charcoal mb-2">
                                    {memory.title}
                                  </h3>
                                  
                                  {memory.description && (
                                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                      {memory.description}
                                    </p>
                                  )}
                                  
                                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <div className="flex items-center gap-3">
                                      {memory.location && (
                                        <span className="flex items-center gap-1">
                                          <MapPin className="w-3 h-3" />
                                          {memory.location}
                                        </span>
                                      )}
                                      {memory.comment_count > 0 && (
                                        <span className="flex items-center gap-1">
                                          <MessageCircle className="w-3 h-3" />
                                          {memory.comment_count}
                                        </span>
                                      )}
                                    </div>
                                    
                                    {(memory.display_date || memory.completed_at) && (
                                      <span className="flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {format(new Date(memory.display_date || memory.completed_at), "MMM d")}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {/* Notes preview */}
                                  {memory.notes && (
                                    <div className="mt-3 p-3 bg-accent/10 rounded-lg">
                                      <p className="text-xs text-charcoal italic line-clamp-2">
                                        "{memory.notes}"
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                </div>
              )}
              </>
              ) : (
                /* Shared Scrapbook Tab */
                sharedLoading ? (
                  <div className="text-center py-12 text-muted-foreground">Loading shared memories...</div>
                ) : !sharedMeta.partner_name ? (
                  <div className="text-center py-12">
                    <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-muted-foreground">Link a partner to see shared memories</p>
                  </div>
                ) : sharedScrapbook.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="w-12 h-12 text-pink-500/30 mx-auto mb-3" />
                    <h3 className="font-serif text-lg font-medium text-foreground mb-2">No shared memories yet</h3>
                    <p className="text-sm text-muted-foreground">
                      Visit the same countries as {sharedMeta.partner_name} to build shared memories
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 text-sm text-pink-500 bg-pink-500/10 px-3 py-2 rounded-lg">
                      <Heart className="w-4 h-4" />
                      {sharedMeta.shared_trips ?? sharedScrapbook.length} shared {(sharedMeta.shared_trips ?? sharedScrapbook.length) === 1 ? "trip" : "trips"} with {sharedMeta.partner_name}
                      {sharedMeta.shared_countries > 0 && (
                        <span className="text-pink-500/70">
                          · {sharedMeta.shared_countries} {sharedMeta.shared_countries === 1 ? "country" : "countries"}
                        </span>
                      )}
                    </div>
                    {sharedScrapbook.map((memory, idx) => (
                      <div key={memory.id} className="rounded-xl overflow-hidden border border-border bg-card">
                        {memory.photos?.length > 0 && (
                          <div className="h-40 overflow-hidden relative">
                            <AuthedImage
                              photoId={memory.cover_photo_id || memory.photos[0].id}
                              alt={memory.title}
                              className="w-full h-full"
                              eager={idx < 2}
                            />
                            <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium bg-black/60 text-white">
                              {memory.is_mine ? "You" : sharedMeta.partner_name}
                            </div>
                          </div>
                        )}
                        <div className="p-4">
                          <h4 className="font-medium text-foreground">{memory.title}</h4>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            {memory.country && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{memory.country}</span>}
                            {memory.display_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{format(new Date(memory.display_date), "MMM d, yyyy")}</span>}
                            <span className="flex items-center gap-1"><ImageIcon className="w-3 h-3" />{memory.photo_count}</span>
                          </div>
                          {memory.notes && (
                            <p className="mt-2 text-xs text-muted-foreground italic line-clamp-2">"{memory.notes}"</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Slideshow Dialog */}
      <Dialog open={showSlideshow} onOpenChange={() => setShowSlideshow(false)}>
        <DialogContent className="max-w-5xl bg-black border-none rounded-2xl p-0 h-[85vh] overflow-hidden">
          <div className="relative w-full h-full flex items-center justify-center">
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-4 right-4 z-10 bg-black/40 hover:bg-black/60 text-white"
              onClick={() => setShowSlideshow(false)}
            >
              <X className="w-5 h-5" />
            </Button>
            
            {slideshowPhotos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-4 z-10 bg-black/40 hover:bg-black/60 text-white h-12 w-12"
                  onClick={prevSlide}
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-4 z-10 bg-black/40 hover:bg-black/60 text-white h-12 w-12"
                  onClick={nextSlide}
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}
            
            {slideshowPhotos[slideshowIndex] && (
              <img
                src={slideshowPhotos[slideshowIndex].url}
                alt={slideshowPhotos[slideshowIndex].caption || "Photo"}
                className="max-w-full max-h-full object-contain"
              />
            )}
            
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-between text-white">
                <p className="text-sm">
                  {slideshowPhotos[slideshowIndex]?.caption || ""}
                </p>
                <span className="text-sm opacity-70">
                  {slideshowIndex + 1} / {slideshowPhotos.length}
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Memory Detail Dialog */}
      <Dialog open={!!selectedMemory && !showSlideshow} onOpenChange={() => setSelectedMemory(null)}>
        <DialogContent className="max-w-2xl bg-card border-none rounded-2xl p-0 max-h-[85vh] overflow-hidden">
          {selectedMemory && (
            <>
              {selectedMemory.photos?.length > 0 && (
                <div className="relative h-56 overflow-hidden">
                  <AuthedImage
                    photoId={selectedMemory.cover_photo_id || selectedMemory.photos[0].id}
                    alt={selectedMemory.title}
                    className="w-full h-full"
                    imgStyle={getImageTransform(imagePositions[selectedMemory.id])}
                    eager
                    variant="download"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 text-white"
                    onClick={() => setSelectedMemory(null)}
                  >
                    <X className="w-5 h-5" />
                  </Button>
                  
                  <div className="absolute bottom-4 left-6 right-6 text-white">
                    <h2 className="font-serif text-2xl font-medium mb-1">
                      {selectedMemory.title}
                    </h2>
                    <div className="flex items-center gap-3 text-sm opacity-80">
                      {selectedMemory.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {selectedMemory.location}, {selectedMemory.country}
                        </span>
                      )}
                      {selectedMemory.completed_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(selectedMemory.completed_at), "MMMM d, yyyy")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <ScrollArea className="max-h-[50vh]">
                <div className="p-6 space-y-6">
                  {selectedMemory.description && (
                    <p className="text-muted-foreground">
                      {selectedMemory.description}
                    </p>
                  )}
                  
                  {selectedMemory.notes && (
                    <div className="p-4 bg-accent/10 rounded-xl">
                      <h4 className="font-medium text-charcoal mb-2">Notes</h4>
                      <p className="text-sm text-charcoal italic">
                        "{selectedMemory.notes}"
                      </p>
                    </div>
                  )}
                  
                  {selectedMemory.photo_count > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-charcoal">
                          Photos ({selectedMemory.photo_count})
                        </h4>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchSlideshowPhotos(selectedMemory)}
                          className="rounded-full"
                        >
                          <Maximize2 className="w-4 h-4 mr-2" />
                          View Slideshow
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  {selectedMemory.comments && selectedMemory.comments.length > 0 && (
                    <div>
                      <h4 className="font-medium text-charcoal mb-3">
                        Comments ({selectedMemory.comment_count})
                      </h4>
                      <div className="space-y-3">
                        {selectedMemory.comments.map((comment) => (
                          <div key={comment.id || comment.at} className="p-3 bg-background rounded-lg">
                            <p className="text-sm text-charcoal">{comment.text}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              — {comment.by}, {format(new Date(comment.at), "MMM d")}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground pt-4 border-t border-charcoal/5">
                    <span>Added by {selectedMemory.created_by_name}</span>
                    {selectedMemory.is_group_item && (
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        Group activity
                      </span>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Position Editor */}
      <ImagePositionEditor
        isOpen={!!editingImage}
        onClose={() => setEditingImage(null)}
        imageUrl={editingImage?.imageUrl}
        initialPosition={editingImage ? (imagePositions[editingImage.memoryId] || { x: 0, y: 0, scale: 1 }) : { x: 0, y: 0, scale: 1 }}
        onSave={(position) => editingImage && handleSavePosition(editingImage.memoryId, position)}
        title="Adjust Photo Position"
      />

      {/* Cover Photo Selection Modal */}
      <Dialog open={!!selectingCover} onOpenChange={() => setSelectingCover(null)}>
        <DialogContent className="max-w-2xl bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Choose Cover Photo
            </DialogTitle>
            <DialogDescription>
              Select a photo to use as the cover for "{selectingCover?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-3 gap-3 mt-4">
            {selectingCover?.photos?.map((photo) => (
              <CoverPhotoOption
                key={photo.id}
                photo={photo}
                isSelected={selectingCover?.cover_photo_id === photo.id}
                onSelect={() => setCoverPhoto(selectingCover.id, photo.id)}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Component for cover photo selection — uses the shared AuthedImage so
// re-opening this modal doesn't re-fetch (cache hit at the module level).
const CoverPhotoOption = ({ photo, isSelected, onSelect }) => (
  <button
    onClick={onSelect}
    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
      isSelected
        ? "border-amber-500 ring-2 ring-amber-500/30"
        : "border-transparent hover:border-primary/50"
    }`}
  >
    <AuthedImage photoId={photo.id} alt="" className="w-full h-full" eager />

    {isSelected && (
      <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
          <Check className="w-5 h-5 text-white" />
        </div>
      </div>
    )}
  </button>
);

export default Scrapbook;
